// src/lignesLibresPresta.js
// ══════════════════════════════════════════════════════════════════════════
// Lignes libres de la facture « prestations » (Facturation › 🧹 Prestations).
// Logique pure, sans React : validation d'une ligne saisie à la main et insertion
// dans les sections construites par FacturationPrestations.construireSections.
//
// Décisions de Claire (19/10/2026) : ligne ajoutée dans l'écran Prestations avant
// génération (PDF inchangé) ; prix négatif autorisé (remise, geste commercial) ;
// avertissement si le libellé contient un terme interdit par la règle carte G.
// Une ligne libre n'est liée à AUCUNE tâche : elle n'entre jamais dans lignesIds
// (donc l'anti-double-facturation par tâche n'est pas touchée).
// ══════════════════════════════════════════════════════════════════════════

export const SECTIONS_LIBRES = ['Prestations annexes', 'Ménages', 'Check-in'];
// Ordre d'affichage fixe de la facture prestations (gabarit figé, SPEC_facturation_prestations_v1).
export const ORDRE_SECTIONS = ['Ménages', 'Check-in', 'Prestations annexes', 'Majoration'];
export const TERMES_INTERDITS = ['commission', 'honoraires', 'honoraire', 'gestion locative', 'releve de gerance'];

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const sansAccents = (s) => String(s == null ? '' : s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Termes interdits (carte G) présents dans un libellé — insensible à la casse et aux accents.
export function termesInterditsDans(libelle) {
  const t = sansAccents(libelle);
  return TERMES_INTERDITS.filter((m) => t.includes(m)).filter((m, i, a) => !(m === 'honoraire' && a.includes('honoraires')));
}

// Nombre saisi à la française ("12,5", " -8 ", "1 250,00") → nombre, ou NaN.
export function lireNombre(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const s = String(v == null ? '' : v).replace(/[\s\u00a0\u202f]/g, '').replace(',', '.').trim();
  if (s === '' || !/^[-+]?\d*\.?\d+$/.test(s)) return NaN;
  return Number(s);
}

// Valide une saisie. Renvoie { ok:true, ligne } ou { ok:false, erreur }.
// `dtLabel` = libellé de date déjà formaté par l'appelant (ex. « Lun 19/10 »), '' si pas de date.
export function validerLigneLibre({ libelle, quantite, prix, section, dtLabel }) {
  const d = String(libelle == null ? '' : libelle).trim().replace(/\s+/g, ' ');
  if (!d) return { ok: false, erreur: 'Saisis un libellé pour la ligne.' };
  if (d.length > 120) return { ok: false, erreur: 'Libellé trop long (120 caractères maximum).' };
  const q = quantite === '' || quantite == null ? 1 : lireNombre(quantite);
  if (!Number.isFinite(q) || q === 0) return { ok: false, erreur: 'La quantité doit être un nombre différent de 0.' };
  if (Math.abs(q) > 9999) return { ok: false, erreur: 'Quantité trop grande.' };
  const pu = lireNombre(prix);
  if (!Number.isFinite(pu)) return { ok: false, erreur: 'Saisis un prix unitaire HT (nombre, négatif possible pour une remise).' };
  if (pu === 0) return { ok: false, erreur: 'Le prix unitaire ne peut pas être 0.' };
  if (Math.abs(pu) > 100000) return { ok: false, erreur: 'Prix unitaire trop grand.' };
  const sec = SECTIONS_LIBRES.includes(section) ? section : 'Prestations annexes';
  return {
    ok: true,
    ligne: { d, dt: dtLabel || '', q, pu: r2(pu), section: sec },
  };
}

// Insère les lignes libres dans les sections déjà construites (sans les modifier).
// Renvoie { sections, total } : sections dans l'ordre fixe du gabarit, total HT arrondi.
// Une ligne à prix négatif n'est jamais comptée comme « unité » (sous-total en nombre d'unités du PDF).
export function ajouterLignesLibres(sections, libres) {
  const base = (sections || []).map((s) => ({ ...s, lignes: [...(s.lignes || [])] }));
  (libres || []).forEach((l) => {
    let sec = base.find((s) => s.lbl === l.section);
    if (!sec) { sec = { lbl: l.section, lignes: [] }; base.push(sec); }
    sec.lignes.push({ d: l.d, dt: l.dt || '', q: l.q, pu: l.pu, unite: l.pu > 0, note: '', libre: true });
  });
  const rang = (lbl) => { const i = ORDRE_SECTIONS.indexOf(lbl); return i < 0 ? ORDRE_SECTIONS.length : i; };
  const ordonnees = base
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rang(a.s.lbl) - rang(b.s.lbl) || a.i - b.i)
    .map((x) => x.s)
    .filter((s) => s.lignes.length > 0);
  const total = r2(ordonnees.reduce((a, s) => a + s.lignes.reduce((b, l) => b + l.pu * l.q, 0), 0));
  return { sections: ordonnees, total };
}

// Total HT des seules lignes libres (aperçu en direct).
export function totalLignesLibres(libres) {
  return r2((libres || []).reduce((a, l) => a + l.pu * l.q, 0));
}
